import React, { useState } from "react";
import { useModalState } from "../../helpers/ModalProvider";
import axios from "axios";
import { env } from "../../helpers/GlobalStateProvider";
import { getApiUrl } from "../../config";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Input, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";

const ResetPasswordModal: React.FC = () => {
  const { isResetPasswordModalVisible, closeResetPasswordModal } = useModalState();

  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const requestPasswordReset = async (enteredEmail: string) => {
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/request_password_reset`,
        { email: enteredEmail },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error requesting password reset:", error);
      throw error;
    }
  };

  const handleRequestPasswordReset = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setRequestSent(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      await requestPasswordReset(email);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    setEmail("");
    setRequestSent(false);
    closeResetPasswordModal();
  };

  return (
    <DialogRoot lazyMount open={isResetPasswordModalVisible} onOpenChange={(e) => { if (!e.open) onCancel(); }}>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isLoading && !requestSent) {
            e.preventDefault();
            handleRequestPasswordReset();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{requestSent ? "Check your inbox" : "Reset password"}</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          {requestSent ? (
            <Text fontSize="md" color="#52525b" lineHeight="24px">
              An email with a link to reset your password was sent to the email
              address associated with your account.
            </Text>
          ) : (
            <Stack gap={4}>
              <Text fontSize="md" color="#52525b" lineHeight="24px">
                Enter your email address and we&rsquo;ll send you a link to reset
                your password.
              </Text>
              <Field label="Email address">
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fontSize="sm"
                />
              </Field>
            </Stack>
          )}
        </DialogBody>
        <DialogFooter>
          {requestSent ? (
            <Button colorPalette="purple" bg="#9333ea" onClick={handleResendLink} loading={isLoading}>
              Resend link
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onCancel}>
                Go back
              </Button>
              <Button colorPalette="purple" bg="#9333ea" onClick={handleRequestPasswordReset} loading={isLoading}>
                Reset password
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ResetPasswordModal;
